import React, { useMemo, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Tooltip,
  IconButton,
  Menu,
  Skeleton,
  Alert,
  Autocomplete,
} from '@mui/material';
import { Add as AddIcon, MoreVert as MoreIcon } from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import StatusPill from './StatusPill';
import {
  useClientRequestsList,
  useDeleteRequest,
  useRemindRequest,
  useCancelRequest,
  useSendRequest,
} from '../../hooks/useClientRequests';
import axios from 'axios';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'partially_complete', label: 'Partial' },
  { value: 'complete', label: 'Complete' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
];

function TableSkeletonRows({ cols }) {
  return [...Array(6)].map((_, i) => (
    <TableRow key={i}>
      {[...Array(cols)].map((__, j) => (
        <TableCell key={j}>
          <Skeleton variant="text" width={j === 0 ? 160 : 80} />
        </TableCell>
      ))}
    </TableRow>
  ));
}

export default function ClientRequestList({
  clientId,
  onNew,
  onSelect,
  initialStatuses = [],
  dueAfter,
  dueBefore,
  filterClientId,
}) {
  const navigate = useNavigate();
  const [statusValues, setStatusValues] = useState(initialStatuses);
  const [dueStart, setDueStart] = useState(dueAfter || '');
  const [dueEnd, setDueEnd] = useState(dueBefore || '');
  const [clientFilter, setClientFilter] = useState(null);
  const [clients, setClients] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  React.useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!clientId) {
      axios
        .get('/api/clients/', { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setClients(res.data?.results || res.data || []))
        .catch(() => setClients([]));
    }
  }, [clientId]);

  React.useEffect(() => {
    if (!filterClientId || !clients.length) return;
    const m = clients.find((c) => c.id === filterClientId);
    if (m) setClientFilter(m);
  }, [filterClientId, clients]);

  React.useEffect(() => {
    setStatusValues(initialStatuses || []);
  }, [initialStatuses]);

  React.useEffect(() => {
    setDueStart(dueAfter || '');
    setDueEnd(dueBefore || '');
  }, [dueAfter, dueBefore]);

  const statusParam = useMemo(() => {
    if (!statusValues?.length) return undefined;
    return statusValues.join(',');
  }, [statusValues]);

  const effectiveClientFilter = clientId || filterClientId || clientFilter?.id;

  const listFilters = useMemo(
    () => ({
      clientId: effectiveClientFilter,
      status: statusParam,
      dueAfter: dueStart || undefined,
      dueBefore: dueEnd || undefined,
    }),
    [effectiveClientFilter, statusParam, dueStart, dueEnd]
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useClientRequestsList(listFilters);

  const requests = data?.results ?? [];

  const deleteMutation = useDeleteRequest();
  const remindMutation = useRemindRequest();
  const cancelMutation = useCancelRequest();
  const sendMutation = useSendRequest();

  const openMenu = (e, row) => {
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const handleView = () => {
    const id = menuRow?.id;
    closeMenu();
    if (!id) return;
    if (onSelect) onSelect(menuRow);
    else navigate(`/client-requests/${id}`);
  };

  const handleRemind = async () => {
    const id = menuRow?.id;
    closeMenu();
    if (!id) return;
    try {
      await remindMutation.mutateAsync(id);
      refetch();
    } catch {
      /* toast elsewhere */
    }
  };

  const handleCancel = async () => {
    const id = menuRow?.id;
    closeMenu();
    if (!id) return;
    try {
      await cancelMutation.mutateAsync(id);
      refetch();
    } catch {
      /* */
    }
  };

  const handleDelete = async () => {
    const id = menuRow?.id;
    closeMenu();
    if (!id) return;
    try {
      await deleteMutation.mutateAsync(id);
      refetch();
    } catch {
      /* */
    }
  };

  const handleSend = async () => {
    const id = menuRow?.id;
    closeMenu();
    if (!id) return;
    try {
      await sendMutation.mutateAsync(id);
      refetch();
    } catch {
      /* */
    }
  };

  const colCount = clientId ? 6 : 7;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              label="Status"
              value={statusValues}
              onChange={(e) => setStatusValues(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(selected) =>
                selected.length === 0 ? 'All' : selected.map((s) => STATUS_OPTIONS.find((o) => o.value === s)?.label || s).join(', ')
              }
            >
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!clientId && (
            <Autocomplete
              sx={{ minWidth: 240 }}
              size="small"
              options={clients}
              getOptionLabel={(o) => o.display_name || `${o.first_name || ''} ${o.last_name || ''}`.trim()}
              value={clients.find((c) => c.id === clientFilter?.id) || null}
              onChange={(_, v) => setClientFilter(v)}
              renderInput={(params) => <TextField {...params} label="Client" />}
              isOptionEqualToValue={(a, b) => a.id === b.id}
            />
          )}
          <TextField
            size="small"
            label="Due after"
            type="date"
            value={dueStart}
            onChange={(e) => setDueStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Due before"
            type="date"
            value={dueEnd}
            onChange={(e) => setDueEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button size="small" variant="outlined" onClick={() => refetch()} disabled={isFetching}>
            Apply
          </Button>
        </Box>
        {onNew && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onNew}>
            New Request
          </Button>
        )}
      </Box>

      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {error?.message || 'Failed to load requests.'}
        </Alert>
      )}

      {isLoading ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                {!clientId && <TableCell sx={{ fontWeight: 700 }}>Client</TableCell>}
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Progress</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last reminder</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableSkeletonRows cols={colCount} />
            </TableBody>
          </Table>
        </TableContainer>
      ) : requests.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 6, px: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No requests yet — create one to get started.
          </Typography>
          {onNew && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onNew}>
              New Request
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                {!clientId && <TableCell sx={{ fontWeight: 700 }}>Client</TableCell>}
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Progress</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last reminder</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((req) => {
                const total = req.items_total || 0;
                const fulfilled = req.items_fulfilled || 0;
                const pct = total > 0 ? Math.round((fulfilled / total) * 100) : 0;
                return (
                  <TableRow
                    key={req.id}
                    hover
                    sx={{ cursor: onSelect || !clientId ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (onSelect) onSelect(req);
                      else navigate(`/client-requests/${req.id}`);
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {req.title}
                      </Typography>
                    </TableCell>
                    {!clientId && (
                      <TableCell>
                        <Typography variant="body2">{req.client_name || '—'}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <StatusPill status={req.status} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {total > 0 ? (
                        <Tooltip title={`${fulfilled} of ${total} items complete`}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              sx={{ flex: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                              {fulfilled}/{total}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {req.due_date ? new Date(req.due_date).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {req.last_reminder_sent_at
                          ? formatDistanceToNow(parseISO(req.last_reminder_sent_at), { addSuffix: true })
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" aria-label="actions" onClick={(e) => openMenu(e, req)}>
                        <MoreIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={handleView}>View</MenuItem>
        {menuRow?.status === 'draft' && <MenuItem onClick={handleSend}>Send to client</MenuItem>}
        {['sent', 'in_progress', 'partially_complete', 'overdue'].includes(menuRow?.status) && (
          <MenuItem onClick={handleRemind}>Send reminder</MenuItem>
        )}
        {menuRow?.status !== 'complete' && menuRow?.status !== 'cancelled' && (
          <MenuItem onClick={handleCancel}>Cancel</MenuItem>
        )}
        {menuRow?.status === 'draft' && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
